// frontend/src/api.js

import axios from 'axios';
import config from './config';
import {
  clearAccessToken,
  clearLegacyTokenStorage,
  getAccessToken,
  setAccessToken,
} from './authSession';

const LOCAL_API_HOSTS = new Set(['localhost', '127.0.0.1']);

export function readCookie(name) {
  if (typeof document === 'undefined' || !document.cookie) {
    return '';
  }
  const prefix = `${name}=`;
  const parts = document.cookie.split(';');
  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i].trim();
    if (part.startsWith(prefix)) {
      try {
        return decodeURIComponent(part.slice(prefix.length));
      } catch (_) {
        return part.slice(prefix.length);
      }
    }
  }
  return '';
}

/**
 * Normalize API base URL for local development.
 * Forces http for localhost/127.0.0.1 so callers never build https://localhost:8000
 * (Django runserver does not serve TLS; HTTPS hangs/times out).
 */
export function resolveApiBaseURL(
  envBase = process.env.REACT_APP_API_BASE_URL || config.API_BASE_URL || ''
) {
  let base = String(envBase || '').trim();
  if (!base) {
    return '/api';
  }

  base = base.replace(/\/+$/, '');

  try {
    const absolute = /^https?:\/\//i.test(base) ? base : `http://${base}`;
    const parsed = new URL(absolute);
    if (LOCAL_API_HOSTS.has(parsed.hostname.toLowerCase())) {
      parsed.protocol = 'http:';
      base = `${parsed.protocol}//${parsed.host}${parsed.pathname}`.replace(/\/+$/, '');
    } else {
      base = absolute.replace(/\/+$/, '');
    }
  } catch (_) {
    // Keep the trimmed base if URL parsing fails.
  }

  if (!/\/(api)$/.test(base)) {
    base += '/api';
  }
  return base;
}

const api = axios.create({
  baseURL: resolveApiBaseURL(),
  withCredentials: true // send cookies for httpOnly JWT
});

// This is an interceptor. It's a piece of code that runs
// BEFORE each request is sent.
api.interceptors.request.use(
  config => {
    config.headers = config.headers || {};
    const token = getAccessToken();
    if (token) {
      // If the token exists, add it to the Authorization header
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    const csrf =
      readCookie('csrftoken') ||
      sessionStorage.getItem('csrfToken') ||
      localStorage.getItem('csrfToken');
    if (csrf && !config.headers['X-CSRFToken'] && !config.headers['x-csrftoken']) {
      config.headers['X-CSRFToken'] = csrf;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

export default api;

// Add a response interceptor to handle token refresh on 401
let isRefreshing = false;
let pendingRequests = [];

function storeCsrfToken(response) {
  const csrf =
    (response && response.headers && (response.headers['x-csrftoken'] || response.headers['X-CSRFToken'])) ||
    '';
  if (!csrf) {
    return;
  }
  try {
    sessionStorage.setItem('csrfToken', csrf);
  } catch (_) {
    /* ignore quota / private-mode failures */
  }
}

export async function refreshAccessToken() {
  const normalizedBase = (api.defaults.baseURL || '').replace(/\/+$/, '');
  const refreshResponse = await axios.post(
    `${normalizedBase}/token/refresh/`,
    {},
    { withCredentials: true },
  );
  const newAccessToken = refreshResponse.data && refreshResponse.data.access;
  if (!newAccessToken) {
    throw new Error('Refresh did not return an access token');
  }
  setAccessToken(newAccessToken);
  return newAccessToken;
}

export function logoutClient() {
  clearAccessToken();
  clearLegacyTokenStorage();
  const normalizedBase = (api.defaults.baseURL || '').replace(/\/+$/, '');
  axios.post(`${normalizedBase}/logout/`, {}, { withCredentials: true }).catch(() => {});
}

export async function bootstrapSession() {
  clearLegacyTokenStorage();
  try {
    await refreshAccessToken();
  } catch (_) {
    clearAccessToken();
  }
}

api.interceptors.response.use(
  (response) => {
    storeCsrfToken(response);
    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};
    if (originalRequest.skipAuthRefresh) {
      return Promise.reject(error);
    }
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRequests.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            originalRequest._retry = true;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;
      try {
        const newAccessToken = await refreshAccessToken();

        api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;

        pendingRequests.forEach((pending) => pending.resolve(newAccessToken));
        pendingRequests = [];

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        pendingRequests.forEach((pending) => pending.reject(refreshError));
        pendingRequests = [];
        clearAccessToken();
        clearLegacyTokenStorage();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);
